/**
 * SalesProposalDetailPage — Phase 33.9.
 *
 * Shows all fields for a single sales proposal. Founder-signed approve/reject
 * actions are available when the proposal is in draft or sent status.
 */

import { useState } from 'react';
import { Navigate, useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { signHashWithFounderKey } from '@/api/real/founderSign';
import { apiBaseUrl } from '@/api/http';
import { getAccessToken } from '@/api/supabase';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ProposalResponse {
  id: string;
  lead_id: string;
  product_id: string | null;
  pipeline_id: string | null;
  sections_json: string;
  pricing_json: string | null;
  gi_signature_hex: string | null;
  valid_until: number;
  status: string;
  created_at: number;
}

// ── API ───────────────────────────────────────────────────────────────────────

async function fetchProposal(id: string): Promise<ProposalResponse> {
  const token = await getAccessToken();
  const resp = await fetch(`${apiBaseUrl()}/sales/proposals/${id}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.json() as Promise<ProposalResponse>;
}

function sortKeys(obj: unknown): unknown {
  if (Array.isArray(obj)) return obj.map(sortKeys);
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, v]) => [k, sortKeys(v)]),
    );
  }
  return obj;
}

async function sha256hex(text: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

async function callProposalAction(
  proposalId: string,
  action: 'approve' | 'reject',
  extra?: Record<string, unknown>,
): Promise<void> {
  const body: Record<string, unknown> = { nonce: crypto.randomUUID(), ...extra };
  const canonical = JSON.stringify(sortKeys(body));
  const hashHex = await sha256hex(canonical);
  const sig = await signHashWithFounderKey(hashHex);
  const token = await getAccessToken();
  const resp = await fetch(
    `${apiBaseUrl()}/sales/proposals/${proposalId}/${action}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Founder-Signature': sig,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: canonical,
    },
  );
  if (!resp.ok) {
    const err = (await resp.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `HTTP ${resp.status}`);
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const PROPOSAL_STATUS_COLORS: Record<string, string> = {
  draft:    '#7dd3fc',
  sent:     '#a78bfa',
  approved: '#6ee7b7',
  rejected: '#f87171',
  expired:  '#fbbf24',
};

export function proposalStatusColor(s: string): string {
  return PROPOSAL_STATUS_COLORS[s] ?? 'var(--omega-ink-subtle)';
}

export function fmtTime(unix: number): string {
  return new Date(unix * 1000).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export function parseSections(json: string): Record<string, unknown> | null {
  try {
    const v = JSON.parse(json) as unknown;
    if (v && typeof v === 'object' && !Array.isArray(v)) return v as Record<string, unknown>;
    return null;
  } catch {
    return null;
  }
}

// ── Sub-components ────────────────────────────────────────────────────────────

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '160px 1fr',
        gap: 8,
        padding: '8px 0',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        alignItems: 'flex-start',
      }}
    >
      <span
        style={{
          fontSize: '8pt',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--omega-ink-subtle)',
          paddingTop: 1,
        }}
      >
        {label}
      </span>
      <span style={{ fontSize: '9pt', color: 'var(--omega-ink)', wordBreak: 'break-all' }}>
        {children}
      </span>
    </div>
  );
}

function JsonBlock({ value }: { value: Record<string, unknown> }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {Object.entries(value).map(([k, v]) => (
        <div key={k} style={{ display: 'flex', gap: 8, fontSize: '8.5pt' }}>
          <span style={{ color: 'var(--omega-ink-subtle)', minWidth: 120, flexShrink: 0 }}>{k}:</span>
          <span style={{ color: 'var(--omega-ink)', wordBreak: 'break-all' }}>
            {typeof v === 'object' ? JSON.stringify(v) : String(v)}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Action bar ────────────────────────────────────────────────────────────────

function ActionBar({
  proposal,
  onAction,
  busy,
  error,
}: {
  proposal: ProposalResponse;
  onAction: (action: 'approve' | 'reject', extra?: Record<string, unknown>) => void;
  busy: boolean;
  error: string | null;
}) {
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const isActionable = proposal.status === 'draft' || proposal.status === 'sent';

  const btnBase: React.CSSProperties = {
    fontFamily: 'var(--omega-mono)',
    fontSize: '9pt',
    fontWeight: 600,
    border: 'none',
    borderRadius: 4,
    padding: '7px 16px',
    cursor: busy ? 'not-allowed' : 'pointer',
    opacity: busy ? 0.6 : 1,
  };

  return (
    <div
      style={{
        position: 'sticky',
        bottom: 0,
        background: 'var(--omega-bg, #0a0d12)',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        padding: '12px 0 8px',
        marginTop: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      {error && <div style={{ color: '#f87171', fontSize: '8.5pt' }}>{error}</div>}

      {showReject && (
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            autoFocus
            placeholder="Rejection reason (optional)…"
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            style={{
              flex: 1,
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 4,
              color: 'var(--omega-ink)',
              fontFamily: 'var(--omega-mono)',
              fontSize: '9pt',
              padding: '5px 10px',
            }}
            onKeyDown={e => {
              if (e.key === 'Escape') { setShowReject(false); setRejectReason(''); }
              if (e.key === 'Enter') { onAction('reject', { reason: rejectReason }); setShowReject(false); }
            }}
          />
          <button
            onClick={() => { onAction('reject', { reason: rejectReason }); setShowReject(false); }}
            disabled={busy}
            style={{ ...btnBase, background: '#f87171', color: '#000' }}
          >
            Confirm Reject
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {isActionable && (
          <>
            <button
              onClick={() => onAction('approve')}
              disabled={busy}
              style={{ ...btnBase, background: '#6ee7b7', color: '#000' }}
            >
              {busy ? 'Signing…' : 'Approve'}
            </button>
            <button
              onClick={() => setShowReject(s => !s)}
              disabled={busy}
              style={{ ...btnBase, background: 'rgba(248,113,113,0.12)', color: '#f87171', border: '1px solid rgba(248,113,113,0.3)' }}
            >
              Reject
            </button>
          </>
        )}

        {!isActionable && (
          <div
            style={{
              padding: '6px 14px',
              borderRadius: 4,
              background: `rgba(${proposal.status === 'approved' ? '110,231,183' : '248,113,113'},0.08)`,
              color: proposalStatusColor(proposal.status),
              border: `1px solid ${proposalStatusColor(proposal.status)}44`,
              fontSize: '8.5pt',
              letterSpacing: '0.04em',
            }}
          >
            {proposal.status.toUpperCase()}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function SalesProposalDetailPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isPending, isError } = useQuery({
    queryKey: ['sales', 'proposal', id],
    queryFn: () => fetchProposal(id),
    refetchInterval: 15_000,
    enabled: !!id,
  });

  const actionMutation = useMutation({
    mutationFn: ({ action, extra }: { action: 'approve' | 'reject'; extra?: Record<string, unknown> }) =>
      callProposalAction(id, action, extra),
    onSuccess: () => {
      setActionError(null);
      void qc.invalidateQueries({ queryKey: ['sales', 'proposal', id] });
    },
    onError: (err: Error) => setActionError(err.message),
  });

  const proposal = data;

  const sectionsObj = proposal ? parseSections(proposal.sections_json) : null;
  const pricingObj = proposal?.pricing_json ? parseSections(proposal.pricing_json) : null;

  return (
    <div
      className="omega-shell"
      style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}
    >
      <Breadcrumbs
        crumbs={[
          { label: 'OMEGA', to: '/omega' },
          { label: 'Sales', to: '/omega/sales' },
          { label: 'Proposals', to: '/omega/sales/proposals' },
          { label: id.slice(0, 8) },
        ]}
      />

      {isPending && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', marginTop: 16 }}>Loading…</div>
      )}
      {isError && (
        <div style={{ color: '#f87171', fontSize: '9pt', marginTop: 16 }}>Failed to load proposal.</div>
      )}

      {proposal && (
        <>
          {/* Header */}
          <div style={{ marginTop: 8, marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
              <h1 style={{ margin: 0, fontSize: '13pt', fontWeight: 700, letterSpacing: '0.04em', fontFamily: 'monospace' }}>
                {proposal.id.slice(0, 8)}
              </h1>
              <span
                style={{
                  fontSize: '7.5pt',
                  padding: '1px 6px',
                  border: `1px solid ${proposalStatusColor(proposal.status)}`,
                  color: proposalStatusColor(proposal.status),
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  borderRadius: 3,
                }}
              >
                {proposal.status}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 14, fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', flexWrap: 'wrap' }}>
              <span>Created {fmtTime(proposal.created_at)}</span>
              <span>Valid until {fmtTime(proposal.valid_until)}</span>
            </div>
          </div>

          {/* Detail fields */}
          <div
            style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.07)',
              borderRadius: 6,
              padding: '0 16px',
              marginBottom: 20,
            }}
          >
            <FieldRow label="ID">
              <span style={{ fontFamily: 'monospace', fontSize: '8.5pt' }}>{proposal.id}</span>
            </FieldRow>

            <FieldRow label="Lead">
              <Link
                to={`/omega/sales/leads/${proposal.lead_id}`}
                style={{ color: '#7dd3fc', textDecoration: 'none', fontFamily: 'monospace', fontSize: '8.5pt' }}
              >
                {proposal.lead_id}
              </Link>
            </FieldRow>

            {proposal.product_id && (
              <FieldRow label="Product">
                <span style={{ fontFamily: 'monospace', fontSize: '8.5pt' }}>{proposal.product_id}</span>
              </FieldRow>
            )}

            {proposal.pipeline_id && (
              <FieldRow label="Pipeline">
                <span style={{ fontFamily: 'monospace', fontSize: '8.5pt' }}>{proposal.pipeline_id}</span>
              </FieldRow>
            )}

            {proposal.gi_signature_hex && (
              <FieldRow label="GI Signature">
                <span style={{ fontFamily: 'monospace', fontSize: '8pt', color: 'var(--omega-ink-subtle)' }}>
                  {proposal.gi_signature_hex.slice(0, 24)}…
                </span>
              </FieldRow>
            )}
          </div>

          {/* Sections */}
          <div style={{ marginBottom: 20 }}>
            <div
              style={{
                fontSize: '8.5pt',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--omega-ink-subtle)',
                marginBottom: 10,
              }}
            >
              Sections
            </div>
            <div
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: 6,
                padding: '12px 16px',
              }}
            >
              {sectionsObj ? (
                <JsonBlock value={sectionsObj} />
              ) : (
                <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink)', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                  {proposal.sections_json}
                </div>
              )}
            </div>
          </div>

          {/* Pricing */}
          {proposal.pricing_json && (
            <div style={{ marginBottom: 20 }}>
              <div
                style={{
                  fontSize: '8.5pt',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'var(--omega-ink-subtle)',
                  marginBottom: 10,
                }}
              >
                Pricing
              </div>
              <div
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: 6,
                  padding: '12px 16px',
                }}
              >
                {pricingObj ? (
                  <JsonBlock value={pricingObj} />
                ) : (
                  <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink)', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    {proposal.pricing_json}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action bar */}
          <ActionBar
            proposal={proposal}
            onAction={(action, extra) =>
              actionMutation.mutate({ action, ...(extra !== undefined ? { extra } : {}) })
            }
            busy={actionMutation.isPending}
            error={actionError}
          />
        </>
      )}
    </div>
  );
}
